import React from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    disabled,
    children,
    ...props
  }, ref) => {
    const baseStyles = clsx(
      'inline-flex items-center justify-center font-medium transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none',
      'rounded-lg border'
    );

    const variants = {
      primary: clsx(
        'bg-primary-600 text-white border-primary-600',
        'hover:bg-primary-700 hover:border-primary-700',
        'focus:ring-primary-500',
        'disabled:bg-primary-300 disabled:border-primary-300'
      ),
      secondary: clsx(
        'bg-gray-100 text-gray-900 border-gray-200',
        'hover:bg-gray-200 hover:border-gray-300',
        'focus:ring-gray-500',
        'disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200'
      ),
      outline: clsx(
        'bg-transparent text-primary-600 border-primary-600',
        'hover:bg-primary-50 hover:text-primary-700',
        'focus:ring-primary-500',
        'disabled:text-primary-300 disabled:border-primary-300'
      ),
      ghost: clsx(
        'bg-transparent text-gray-700 border-transparent',
        'hover:bg-gray-100 hover:text-gray-900',
        'focus:ring-gray-500',
        'disabled:text-gray-400'
      ),
      danger: clsx(
        'bg-error-600 text-white border-error-600',
        'hover:bg-error-700 hover:border-error-700',
        'focus:ring-error-500',
        'disabled:bg-error-300 disabled:border-error-300'
      ),
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
    };

    const widthClass = fullWidth ? 'w-full' : '';
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variants[variant],
          sizes[size],
          widthClass,
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {isLoading && (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;